import { useEffect, useState } from 'react';
import { ProductCategoryKeys } from '../../../common/enums/common';
import useApi from '../../../common/hooks/useApi';
import useServiceClients from '../../../common/hooks/useServiceClients';
import { CartProps, useCartProvider } from '../../../providers/cart-provider';
import { TyConfig, useConfig } from '../../../providers/config-provider';
import { useContent } from '../../../providers/content/hook';
import { useUserProfile } from '../../../providers/user-provider';
import { getCartItemsByProductType } from '../../../providers/utils/cartHelper';
import { ProductTypes } from '../../../services/helper/cart/constants';
import { IQueryFilter, ProductFilterType } from '../../../services/helper/pdp/frames/productUtils';
import { IProduct } from '../../../services/helper/productInterfaces';
import {
  filterUndefinedPrcingProducts,
  reshapeProductFrames,
} from '../../../services/helper/reshapeProductFrames';
import ProductService from '../../../services/product';
import { usePersonalizeStore } from '../../../stores/personalizeStore';
import { TyRegionBasic } from '../../../types';
import { getProductIdFromUrl } from '../../../utils/browserUtils';
import { safeGet } from '../../../utils/objectUtils';
import { getRegionFromUri, getUserRegion } from '../../../utils/regionUtils';

export const useSimilarProducts = (
  prodType = ProductFilterType.NEW_ARRIVALS,
  productCategory = ProductCategoryKeys.EYEGLASS_FRAMES,
) => {
  const { config } = useConfig();
  const { content } = useContent();
  const { cart } = useCartProvider() as CartProps;
  const { userProfile, liveSessionData } = useUserProfile();
  const { ctproxyBffClient: ctproxyBffClientWithoutLoader } = useServiceClients(false);
  const [products, setProducts] = useState<IProduct[]>([]);
  const personalization = usePersonalizeStore();
  const { data, request } = useApi((queryFilterObj: IQueryFilter) =>
    ProductService.getProducts(ctproxyBffClientWithoutLoader, queryFilterObj),
  );
  const userRegion = getUserRegion(getRegionFromUri()) as TyRegionBasic;
  const monoPd = liveSessionData?.eg_pd?.distance?.left || liveSessionData?.eg_pd?.distance?.right;
  const queryFilterObj: IQueryFilter = {
    productFilterType: prodType,
    config: config as TyConfig,
    personalizeData: personalization,
    productCategory: productCategory,
    region: userProfile?.region || userRegion,
    sku: '',
    productId: '',
    pd: monoPd,
  };

  // This is to make an api call
  useEffect(() => {
    if (safeGet(content, 'page.pageTitle', '').toLowerCase() === 'cart') {
      const cartItemsByProductType = getCartItemsByProductType(cart?.cartItems);
      if (cartItemsByProductType?.[ProductTypes.FRAME]?.length) {
        request([queryFilterObj]);
      }
    } else {
      request([queryFilterObj]);
    }
  }, [cart?.cartItems]);

  // For setting the data on changing products
  useEffect(() => {
    let results: any = [];
    if (data) {
      results = reshapeProductFrames(safeGet(data, 'data.productProjectionSearch.results', []));
      results = filterUndefinedPrcingProducts(results);
      if (results && prodType === ProductFilterType.SIMILAR_PRODUCTS) {
        const productId = getProductIdFromUrl();
        results = (results || []).filter((x: any) => Object.keys(x)[0] !== productId);
      }
      setProducts(results);
    }
    // TODO: have to delete console
    console.warn(`Similar product response returned: ${results?.length || 0}`);
    return () => {
      setProducts([]);
    };
  }, [data]);

  // TODO: Expose more pops from getProductsApi if required
  return {
    products,
  };
};
